import { brandPalette } from "../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../components/Models/admin-modal-theme";
import { examSubmissionPdfUrl } from "../../../components/student/PdfPreviewModal";
import HomeworkPdfAnnotator from "../reports/homework-pdf-annotator";
import { getAssistantPoints } from "./submission-timing";
import { insertAiFeedbackPages } from "./exam-feedback-pdf";
import { normalizeLlmMath } from "./math-text";
import MathRichText from "./math-rich-text";
import { getQuestionData, getQuestionId } from "./question-item";
import {
  clearCorrectionQuestions,
  correctStudentExamRequest,
  getCorrectionQuestionsRequest,
} from "../../../modules/admin-modules/student-exams/Actions";
import { getStudentExamFile } from "../../../modules/admin-modules/student-exams/Api";
import { getAssistantsRequest } from "../../../modules/admin-modules/teachersAssistant/Actions";
import { getMarkSchemesBySubjectRequest } from "../../../modules/admin-modules/mark-schemes/Actions";
import { markPaperWithAi } from "../../../modules/admin-modules/mark-schemes/Api";
import { buildMarkPaperBody } from "../../../modules/admin-modules/mark-schemes/grader-models";
import { GraderModelFields, useGraderModelSelection } from "./grading-model-field";
import { toast } from "react-toastify";

const modalSelectStyles = {
  ...adminSelectStyles(),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

const QUESTION_IMAGE_BASE = "https://edutopiaonline.com/api";
const DEFAULT_PAGE_RANGE = [1, 20];

const EVALUATION_PRESETS = [
  "Your assignment grade is A*9",
  "Your assignment grade is A*8",
  "Your assignment grade is A*",
  "Your assignment grade is A",
  "Your assignment grade is B",
  "Your assignment is incomplete",
];

const scrollPanelProps = {
  flex: "1 1 0",
  minH: 0,
  minW: 0,
  overflowY: "auto",
  overflowX: "auto",
  pr: 1,
  css: {
    scrollbarWidth: "thin",
    scrollbarColor: "var(--scrollbar-thumb, #5B0D16) transparent",
  },
};

async function fetchExamPdfBytes(filename) {
  const response = await getStudentExamFile(filename);
  return response.data;
}

function hasSubmittedPdf(studentExam) {
  return studentExam?.student_pdf != null && String(studentExam.student_pdf).trim() !== "";
}

function isRegisteredScheme(scheme) {
  const id = scheme?.grader_scheme_id;
  return id !== null && id !== undefined && id !== "" && id !== 0 && id !== "0";
}

function normalizeAiPayload(data) {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.questions)) return data;
  if (data.data && Array.isArray(data.data.questions)) return data.data;
  return null;
}

function deriveQuestionStatus(awarded, max, fallback = "") {
  if (Number.isFinite(awarded) && Number.isFinite(max) && max > 0) {
    if (awarded >= max) return "correct";
    if (awarded <= 0) return "incorrect";
    return "partial";
  }
  return fallback || "";
}

function buildFeedbackItems(aiQuestions, correctionQuestions, corrections) {
  if (Array.isArray(aiQuestions) && aiQuestions.length) {
    return aiQuestions.map((aiQ, index) => {
      const examItem = correctionQuestions?.[index];
      const qid = getQuestionId(examItem);
      const edited = qid != null ? corrections?.[qid] : null;
      const awarded =
        edited?.mark !== "" && edited?.mark != null
          ? Number(edited.mark)
          : Number(aiQ.marks_awarded);
      const max = Number(aiQ.max_marks ?? getQuestionData(examItem)?.mark);
      const marksAwarded = Number.isFinite(awarded) ? awarded : aiQ.marks_awarded;
      const maxMarks = Number.isFinite(max) ? max : aiQ.max_marks;
      return {
        id: aiQ.id || edited?.aiId || `Q${index + 1}`,
        page: Number(aiQ.page ?? edited?.page),
        marks_awarded: marksAwarded,
        max_marks: maxMarks,
        status: deriveQuestionStatus(marksAwarded, maxMarks, aiQ.status),
        feedback: normalizeLlmMath(edited?.comment ?? aiQ.feedback ?? ""),
        correct_answer: normalizeLlmMath(edited?.correct_answer ?? aiQ.correct_answer),
      };
    });
  }

  return (correctionQuestions || [])
    .map((item) => {
      const qid = getQuestionId(item);
      const edited = qid != null ? corrections?.[qid] : null;
      if (!edited?.page) return null;
      const awarded = Number(edited.mark);
      const max = Number(edited.max_marks ?? getQuestionData(item)?.mark);
      return {
        id: edited.aiId || `Q${qid}`,
        page: Number(edited.page),
        marks_awarded: awarded,
        max_marks: max,
        status: deriveQuestionStatus(awarded, max, edited.status),
        feedback: normalizeLlmMath(edited.comment ?? ""),
        correct_answer: normalizeLlmMath(edited.correct_answer),
      };
    })
    .filter(Boolean);
}

const CorrectExamModal = ({
  isOpen,
  onClose,
  studentExam,
  examId,
  examName,
  hideCoverScore = false,
  initialAiPayload = null,
  initialMarkSchemeId = null,
  hideAiSection = false,
  reviewIndex = null,
  reviewTotal = null,
  onSkip = null,
  onSubmitted = null,
  onBackgroundSubmit = null,
  onRefuse = null,
  aiError = null,
}) => {
  const dispatch = useDispatch();
  const correctionQuestions = useSelector((state) => state.adminStudentExams.correctionQuestions);
  const isQuestionsLoading = useSelector((state) => state.adminStudentExams.isQuestionsLoading);
  const isSubmittingCorrection = useSelector((state) => state.adminStudentExams.isSubmittingCorrection);
  const assistants = useSelector((state) => state.teacherassistants.assistants);
  const markSchemes = useSelector((state) => state.markSchemes.markSchemes);

  const [corrections, setCorrections] = useState({});
  const [annotatedFile, setAnnotatedFile] = useState(null);
  const [assistant, setAssistant] = useState("");
  const [evaluation, setEvaluation] = useState("");
  const [selectedMarkSchemeId, setSelectedMarkSchemeId] = useState(null);
  const [limitPages, setLimitPages] = useState(false);
  const [pageRange, setPageRange] = useState(DEFAULT_PAGE_RANGE);
  const [isAiCorrecting, setIsAiCorrecting] = useState(false);
  const [isBuildingPdf, setIsBuildingPdf] = useState(false);
  const [aiQuestions, setAiQuestions] = useState([]);
  const annotatorSaveRef = useRef(null);
  const {
    ocrGroups,
    gradingGroups,
    ocrModel,
    gradingModel,
    onOcrModelChange,
    onGradingModelChange,
    isLoadingModels,
  } = useGraderModelSelection(isOpen && !hideAiSection);

  const hasPdf = hasSubmittedPdf(studentExam);

  const assistantsOptions = assistants?.data?.map((data) => ({
    value: data.id,
    label: data.name,
  }));

  const markSchemeOptions = useMemo(
    () =>
      (Array.isArray(markSchemes) ? markSchemes : [])
        .filter(isRegisteredScheme)
        .map((scheme) => ({
          value: scheme.grader_scheme_id,
          label: scheme.title || scheme.name || `Scheme #${scheme.id}`,
        })),
    [markSchemes]
  );

  useEffect(() => {
    dispatch(getAssistantsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!isOpen || !studentExam?.id) return;
    dispatch(getCorrectionQuestionsRequest(studentExam.id));
    dispatch(getMarkSchemesBySubjectRequest());
  }, [isOpen, studentExam?.id, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    setEvaluation("");
    setAnnotatedFile(null);
    setAssistant("");
    setSelectedMarkSchemeId(initialMarkSchemeId ?? null);
    setLimitPages(false);
    setPageRange(DEFAULT_PAGE_RANGE);
    setIsAiCorrecting(false);
    setIsBuildingPdf(false);
    setAiQuestions([]);
  }, [isOpen, studentExam?.id, initialMarkSchemeId]);

  const handleClose = useCallback(() => {
    setCorrections({});
    setAnnotatedFile(null);
    setAssistant("");
    setEvaluation("");
    setSelectedMarkSchemeId(null);
    setLimitPages(false);
    setPageRange(DEFAULT_PAGE_RANGE);
    setIsAiCorrecting(false);
    setIsBuildingPdf(false);
    setAiQuestions([]);
    dispatch(clearCorrectionQuestions());
    onClose();
  }, [dispatch, onClose]);

  const updateField = useCallback((questionId, field, value) => {
    setCorrections((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      },
    }));
  }, []);

  const applyAiResponseToQuestions = useCallback(
    (payload, options = {}) => {
      const aiList = Array.isArray(payload?.questions) ? payload.questions : [];
      setAiQuestions(aiList);
      if (!aiList.length || !correctionQuestions?.length) {
        if (!options.silent) toast.warn("AI response had no questions to map.");
        return 0;
      }

      const next = {};
      let mappedCount = 0;

      correctionQuestions.forEach((item, index) => {
        const qid = getQuestionId(item);
        if (qid == null) return;

        const prevEntry = {
          mark: "",
          comment: "",
        };
        const aiQ = aiList[index];

        if (!aiQ) {
          next[qid] = prevEntry;
          return;
        }

        const maxMark = Number(getQuestionData(item)?.mark) || 0;
        const awarded = Number(aiQ.marks_awarded);
        const markValue = Number.isFinite(awarded)
          ? String(Math.min(Math.max(awarded, 0), maxMark || awarded))
          : "";

        next[qid] = {
          mark: markValue,
          comment: normalizeLlmMath(aiQ.feedback ?? ""),
          page: Number(aiQ.page) || null,
          aiId: aiQ.id || `Q${index + 1}`,
          status: aiQ.status || "",
          correct_answer: normalizeLlmMath(aiQ.correct_answer ?? ""),
          max_marks: Number(aiQ.max_marks) || maxMark,
        };
        mappedCount += 1;
      });

      setCorrections(next);

      if (payload?.overall_comment) {
        setEvaluation(normalizeLlmMath(payload.overall_comment));
      }

      return mappedCount;
    },
    [correctionQuestions]
  );

  useEffect(() => {
    if (!isOpen || !correctionQuestions?.length) {
      setCorrections({});
      return;
    }
    const initial = {};
    correctionQuestions.forEach((item) => {
      const qid = getQuestionId(item);
      if (qid == null) return;
      initial[qid] = {
        mark: "",
        comment: "",
      };
    });
    setCorrections(initial);
    if (Array.isArray(initialAiPayload?.questions) && initialAiPayload.questions.length) {
      applyAiResponseToQuestions(initialAiPayload, { silent: true });
    }
  }, [isOpen, correctionQuestions, initialAiPayload, applyAiResponseToQuestions]);

  const setMarkClamped = useCallback(
    (questionId, rawValue, maxMark) => {
      if (rawValue === "" || rawValue == null) {
        updateField(questionId, "mark", "");
        return;
      }
      const num = Number(rawValue);
      if (!Number.isFinite(num)) {
        updateField(questionId, "mark", "");
        return;
      }
      const capped = Math.min(Math.max(num, 0), maxMark);
      updateField(questionId, "mark", String(capped));
    },
    [updateField]
  );

  const studentLabel = useMemo(() => {
    const name = studentExam?.student?.name;
    const code = studentExam?.student?.user_name;
    if (name && code) return `${name} (${code})`;
    return name || code || "Student";
  }, [studentExam]);

  const studentName = studentExam?.student?.name || studentExam?.student?.user_name || "Student";

  const studentPdfFilename = studentExam?.student_pdf;
  const pdfUrl = hasPdf ? examSubmissionPdfUrl(studentPdfFilename) : "";

  const feedbackItems = useMemo(
    () => buildFeedbackItems(aiQuestions, correctionQuestions, corrections),
    [aiQuestions, correctionQuestions, corrections]
  );

  const scoreTotals = useMemo(() => {
    if (correctionQuestions?.length) {
      return correctionQuestions.reduce(
        (acc, item) => {
          const qid = getQuestionId(item);
          const max = Number(getQuestionData(item)?.mark) || 0;
          const raw = corrections?.[qid]?.mark;
          const awarded =
            raw !== "" && raw != null && Number.isFinite(Number(raw)) ? Number(raw) : 0;
          acc.awarded += awarded;
          acc.max += max;
          return acc;
        },
        { awarded: 0, max: 0 }
      );
    }
    return (feedbackItems || []).reduce(
      (acc, question) => {
        acc.awarded += Number(question.marks_awarded) || 0;
        acc.max += Number(question.max_marks) || 0;
        return acc;
      },
      { awarded: 0, max: 0 }
    );
  }, [correctionQuestions, corrections, feedbackItems]);

  const pageFeedback = useMemo(() => {
    const grouped = {};
    feedbackItems.forEach((item) => {
      const page = Number(item.page);
      if (!Number.isFinite(page) || page < 1) return;
      if (!grouped[page]) grouped[page] = [];
      grouped[page].push(item);
    });
    return grouped;
  }, [feedbackItems]);

  const resultsCover = useMemo(() => {
    if (!aiQuestions.length) return null;
    return {
      studentName,
      examName: examName || "Exam",
      awarded: scoreTotals.awarded,
      max: scoreTotals.max,
      hideScore: hideCoverScore,
    };
  }, [aiQuestions.length, studentName, examName, scoreTotals, hideCoverScore]);

  const feedbackContextLabel = useMemo(() => {
    const schemeLabel =
      markSchemeOptions.find((opt) => opt.value === selectedMarkSchemeId)?.label || "";
    return [schemeLabel, studentLabel].filter(Boolean).join(" . ");
  }, [markSchemeOptions, selectedMarkSchemeId, studentLabel]);

  const handleCorrectWithAi = async () => {
    if (isAiCorrecting) return;
    if (!selectedMarkSchemeId) {
      toast.error("Select a registered mark scheme.");
      return;
    }
    if (!pdfUrl) {
      toast.error("Student PDF URL is required.");
      return;
    }

    const body = buildMarkPaperBody({
      markSchemeId: selectedMarkSchemeId,
      url: pdfUrl,
      ocrModel,
      gradingModel,
      pages: limitPages ? { from: pageRange[0], to: pageRange[1] } : undefined,
    });

    setIsAiCorrecting(true);
    try {
      const result = await markPaperWithAi(body);
      const payload = normalizeAiPayload(result.data) ?? result.data;
      const mapped = applyAiResponseToQuestions(payload);
      if (mapped > 0) {
        toast.success(`AI marks applied to ${mapped} question(s)`);
      } else {
        toast.success("AI correction response received");
      }
    } catch (error) {
      const details = error?.response?.data ?? {
        message: error?.message || "Failed to correct with AI",
      };
      toast.error(details?.error || details?.message || "Failed to correct with AI");
    } finally {
      setIsAiCorrecting(false);
    }
  };

  const renderAiCorrectSection = () => (
    <Box
      mb={6}
      p={5}
      borderWidth="1px"
      borderColor="var(--border-subtle)"
      borderRadius="xl"
      bg="var(--surface-muted)"
    >
      <Text
        fontSize="xs"
        fontWeight="800"
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.1em"
        mb={3}
      >
        Correct with AI
      </Text>

      <Stack spacing={4}>
        <Box>
          <FormLabel fontSize="xs" fontWeight="700" mb={1}>
            Mark scheme
          </FormLabel>
          <Select
            styles={modalSelectStyles}
            options={markSchemeOptions}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            value={markSchemeOptions.find((opt) => opt.value === selectedMarkSchemeId) || null}
            onChange={(opt) => setSelectedMarkSchemeId(opt?.value ?? null)}
            placeholder={
              markSchemeOptions.length
                ? "Select registered mark scheme"
                : "No registered mark schemes"
            }
            isClearable
            isDisabled={!markSchemeOptions.length}
          />
        </Box>

        <GraderModelFields
          ocrGroups={ocrGroups}
          gradingGroups={gradingGroups}
          ocrModel={ocrModel}
          gradingModel={gradingModel}
          onOcrModelChange={onOcrModelChange}
          onGradingModelChange={onGradingModelChange}
          isDisabled={isAiCorrecting}
          isLoading={isLoadingModels}
        />

        <Checkbox
          isChecked={limitPages}
          onChange={(e) => setLimitPages(e.target.checked)}
          colorScheme="red"
          fontWeight="700"
          fontSize="sm"
        >
          Limit pages (optional)
        </Checkbox>

        {limitPages ? (
          <Box>
            <Flex justify="space-between" mb={2}>
              <Text fontSize="xs" fontWeight="700" color="var(--text-muted)">
                Pages
              </Text>
              <Text fontSize="xs" fontWeight="800" color="var(--text-primary)">
                {pageRange[0]} – {pageRange[1]}
              </Text>
            </Flex>
            <RangeSlider
              min={1}
              max={50}
              step={1}
              value={pageRange}
              onChange={setPageRange}
              colorScheme="red"
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack bg={brandPalette.primary} />
              </RangeSliderTrack>
              <RangeSliderThumb index={0} boxSize={4} />
              <RangeSliderThumb index={1} boxSize={4} />
            </RangeSlider>
          </Box>
        ) : null}

        <Button
          type="button"
          alignSelf="flex-start"
          bg={brandPalette.primary}
          color="white"
          fontWeight="800"
          borderRadius="lg"
          onClick={handleCorrectWithAi}
          isLoading={isAiCorrecting}
          isDisabled={!hasPdf || !markSchemeOptions.length || isAiCorrecting}
          _hover={{ bg: brandPalette.secondary }}
        >
          Correct with AI
        </Button>
      </Stack>
    </Box>
  );

  const renderGradingForm = () => {
    if (isQuestionsLoading) {
      return (
        <VStack py={12}>
          <Spinner size="lg" color={brandPalette.primary} />
          <Text fontSize="sm" color="var(--text-muted)">
            Loading questions…
          </Text>
        </VStack>
      );
    }

    const aiErrorBanner = aiError ? (
      <Box
        mb={6}
        p={4}
        borderWidth="1px"
        borderColor="red.200"
        borderRadius="xl"
        bg="rgba(229, 62, 62, 0.08)"
      >
        <Text fontSize="sm" fontWeight="700" color="red.600">
          AI grading failed for this submission. You can still mark it manually.
        </Text>
        <Text fontSize="xs" color="red.500" mt={1}>
          {aiError}
        </Text>
      </Box>
    ) : null;

    if (!correctionQuestions?.length) {
      return (
        <>
          {aiErrorBanner}
          {hideAiSection ? null : renderAiCorrectSection()}
          <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
            No questions found for this submission.
          </Text>
        </>
      );
    }

    return (
      <Stack spacing={8}>
        {aiErrorBanner}
        {hideAiSection ? null : renderAiCorrectSection()}
        {correctionQuestions.map((item, index) => {
          const question = getQuestionData(item);
          const questionId = getQuestionId(item);
          const maxMark = Number(question?.mark) || 0;
          const entry = corrections[questionId] ?? {
            mark: "",
            comment: "",
          };
          const awardedMark = entry.mark === "" || entry.mark == null ? NaN : Number(entry.mark);
          const isBelowMax =
            Number.isFinite(awardedMark) && maxMark > 0 && awardedMark < maxMark;

          return (
            <Box
              key={questionId ?? index}
              p={5}
              borderWidth="1px"
              borderColor={isBelowMax ? "orange.300" : "var(--border-subtle)"}
              borderRadius="xl"
              bg="var(--surface-muted)"
            >
              <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  color="var(--text-muted)"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                >
                  Question {index + 1} · Max {maxMark} marks
                  {entry.page ? ` · Page ${entry.page}` : ""}
                </Text>
                {isBelowMax ? (
                  <Badge colorScheme="orange" borderRadius="full" px={3} py={1} fontWeight="800">
                    Below max
                  </Badge>
                ) : null}
              </Flex>

              {question?.comprehension ? (
                <Box mb={4}>
                  <Text fontSize="xs" fontWeight="700" color="var(--text-muted)" mb={1}>
                    Passage
                  </Text>
                  <Text fontSize="sm" color="var(--text-primary)" whiteSpace="pre-wrap">
                    {question.comprehension}
                  </Text>
                </Box>
              ) : null}

              {question?.question ? (
                <Box
                  mb={4}
                  fontSize="md"
                  color="var(--text-primary)"
                  sx={{
                    "& p, & span, & div, & li": { color: "inherit" },
                  }}
                  dangerouslySetInnerHTML={{ __html: question.question }}
                />
              ) : null}

              {question?.image ? (
                <Image
                  src={`${QUESTION_IMAGE_BASE}/${question.image}`}
                  alt={`Question ${index + 1}`}
                  maxW="100%"
                  maxH="280px"
                  objectFit="contain"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  mb={4}
                />
              ) : null}

              <Stack spacing={4}>
                <Box maxW="220px">
                  <FormLabel fontSize="xs" fontWeight="700" mb={1}>
                    Mark
                  </FormLabel>
                  <HStack spacing={2} mb={2}>
                    <Button
                      size="xs"
                      borderRadius="lg"
                      bg="green.500"
                      color="white"
                      fontWeight="800"
                      _hover={{ bg: "green.600" }}
                      onClick={() => updateField(questionId, "mark", String(maxMark))}
                    >
                      Correct
                    </Button>
                    <Button
                      size="xs"
                      borderRadius="lg"
                      bg="red.500"
                      color="white"
                      fontWeight="800"
                      _hover={{ bg: "red.600" }}
                      onClick={() => updateField(questionId, "mark", "0")}
                    >
                      Wrong
                    </Button>
                  </HStack>
                  <Input
                    type="number"
                    min={0}
                    max={maxMark}
                    step="0.5"
                    value={entry.mark}
                    onChange={(e) => setMarkClamped(questionId, e.target.value, maxMark)}
                    onBlur={(e) => setMarkClamped(questionId, e.target.value, maxMark)}
                    bg="var(--surface-raised)"
                    borderColor="var(--border-subtle)"
                    borderRadius="lg"
                  />
                  <Text fontSize="xs" color="var(--text-muted)" mt={1} fontWeight="600">
                    Max {maxMark}
                  </Text>
                </Box>
                <Box w="100%">
                  <FormLabel fontSize="xs" fontWeight="700" mb={1}>
                    Comment
                  </FormLabel>
                  <Textarea
                    value={entry.comment}
                    onChange={(e) => updateField(questionId, "comment", e.target.value)}
                    placeholder="Feedback for this question (optional)"
                    minH="140px"
                    bg="var(--surface-raised)"
                    borderColor="var(--border-subtle)"
                    borderRadius="lg"
                  />
                  {entry.comment?.trim() ? (
                    <Box
                      mt={2}
                      p={3}
                      borderRadius="lg"
                      bg="var(--surface-raised)"
                      borderWidth="1px"
                      borderColor="var(--border-subtle)"
                    >
                      <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="var(--text-muted)" mb={1}>
                        MATH PREVIEW
                      </Text>
                      <MathRichText fontSize="sm" color="var(--text-primary)">
                        {entry.comment}
                      </MathRichText>
                    </Box>
                  ) : null}
                </Box>
              </Stack>
            </Box>
          );
        })}

        <Box
          p={5}
          borderWidth="1px"
          borderColor="var(--border-subtle)"
          borderRadius="xl"
          bg="var(--surface-muted)"
        >
          <FormLabel fontSize="xs" fontWeight="700" mb={2}>
            Evaluation (optional)
          </FormLabel>
          <HStack spacing={2} mb={3} flexWrap="wrap">
            {EVALUATION_PRESETS.map((preset) => (
              <Button
                key={preset}
                size="xs"
                borderRadius="lg"
                variant="outline"
                color="var(--text-primary)"
                fontWeight="700"
                bg={evaluation === preset ? "rgba(91, 13, 22, 0.1)" : "var(--surface-raised)"}
                borderWidth={evaluation === preset ? "2px" : "1px"}
                borderColor={evaluation === preset ? brandPalette.primary : "var(--border-subtle)"}
                _hover={{ borderColor: brandPalette.primary }}
                onClick={() => setEvaluation(preset)}
              >
                {preset.replace("Your assignment grade is ", "").replace("Your assignment is ", "")}
              </Button>
            ))}
          </HStack>
          <Textarea
            value={evaluation}
            onChange={(e) => setEvaluation(e.target.value)}
            placeholder="Optional overall evaluation message for the student"
            minH="90px"
            bg="var(--surface-raised)"
            borderColor="var(--border-subtle)"
            borderRadius="lg"
          />
        </Box>
      </Stack>
    );
  };

  const collectCorrectionPayload = useCallback(() => {
    if (!studentExam?.id) return null;

    const payloadCorrections = [];
    for (const item of correctionQuestions) {
      const questionId = getQuestionId(item);
      const question = getQuestionData(item);
      const maxMark = Number(question?.mark) || 0;
      const entry = corrections[questionId] ?? {};
      const markRaw = entry.mark;
      const mark = markRaw === "" || markRaw == null ? NaN : Number(markRaw);

      if (!Number.isFinite(mark) || mark < 0 || mark > maxMark) {
        toast.error(`Enter a valid mark (0–${maxMark}) for each question.`);
        return null;
      }

      payloadCorrections.push({
        question_id: questionId,
        mark,
        comment: entry.comment ?? "",
      });
    }

    if (!payloadCorrections.length) {
      toast.error("No questions to correct.");
      return null;
    }

    const assistantPoints = getAssistantPoints(studentExam);
    const basePayload = {
      student_exam_id: studentExam.id,
      student_id: studentExam.student_id ?? studentExam.student?.id,
      corrections: payloadCorrections,
      exam_id: examId,
      assistant_points: assistantPoints,
    };
    if (assistant) basePayload.assistant_id = assistant;
    const evaluationText = evaluation.trim();
    if (evaluationText) basePayload.evaluation = evaluationText;
    return { basePayload, evaluationText, assistantPoints };
  }, [studentExam, correctionQuestions, corrections, examId, assistant, evaluation]);

  const buildFinalPdfFile = useCallback(async (annotatedOverride = null) => {
    const sourceFile = annotatedOverride || annotatedFile;
    const sourceBytes = sourceFile
      ? await sourceFile.arrayBuffer()
      : studentPdfFilename
        ? await fetchExamPdfBytes(studentPdfFilename)
        : null;
    if (!sourceBytes) return null;
    const merged = await insertAiFeedbackPages(sourceBytes, feedbackItems, {
      watermark: studentName,
      contextLabel: feedbackContextLabel,
      studentName,
      examName: examName || "Exam",
      awarded: scoreTotals.awarded,
      max: scoreTotals.max,
      hideScore: hideCoverScore,
    });
    return new File([merged], studentPdfFilename || "exam.pdf", {
      type: "application/pdf",
    });
  }, [
    annotatedFile,
    studentPdfFilename,
    feedbackItems,
    studentName,
    feedbackContextLabel,
    examName,
    scoreTotals,
    hideCoverScore,
  ]);

  const sendCorrection = useCallback(
    (pdfToUpload) => {
      const collected = collectCorrectionPayload();
      if (!collected) return;
      const { basePayload, evaluationText, assistantPoints } = collected;

      if (pdfToUpload) {
        const formData = new FormData();
        formData.append("student_exam_id", basePayload.student_exam_id);
        formData.append("student_id", basePayload.student_id);
        formData.append("assistant_points", assistantPoints);
        if (basePayload.exam_id != null) formData.append("exam_id", basePayload.exam_id);
        if (assistant) formData.append("assistant_id", assistant);
        if (evaluationText) formData.append("evaluation", evaluationText);
        basePayload.corrections.forEach((correction, index) => {
          formData.append(`corrections[${index}][question_id]`, correction.question_id);
          formData.append(`corrections[${index}][mark]`, correction.mark);
          formData.append(`corrections[${index}][comment]`, correction.comment ?? "");
        });
        formData.append("student_pdf", pdfToUpload);
        dispatch(
          correctStudentExamRequest(
            { formData, exam_id: examId },
            onSubmitted || handleClose
          )
        );
        return;
      }

      dispatch(correctStudentExamRequest(basePayload, onSubmitted || handleClose));
    },
    [collectCorrectionPayload, assistant, dispatch, handleClose, onSubmitted, examId]
  );

  const handleSubmit = async () => {
    const collected = collectCorrectionPayload();
    if (!collected) return;
    if (isBuildingPdf) return;

    let latestAnnotated = annotatedFile;
    if (hasPdf) setIsBuildingPdf(true);

    try {
      if (hasPdf && annotatorSaveRef.current) {
        try {
          const saved = await annotatorSaveRef.current({ silent: true, allowEmpty: true });
          if (saved) {
            latestAnnotated = saved;
            setAnnotatedFile(saved);
          }
        } catch {
          return;
        }
      }

      if (onBackgroundSubmit) {
        onBackgroundSubmit({
          id: studentExam.id,
          studentLabel,
          studentName,
          studentPdfFilename,
          examId,
          examName: examName || "Exam",
          hasPdf,
          collected,
          assistant,
          annotatedFile: latestAnnotated,
          feedbackItems: (feedbackItems || []).map((item) => ({ ...item })),
          feedbackContextLabel,
          scoreTotals: { awarded: scoreTotals.awarded, max: scoreTotals.max },
          hideScore: hideCoverScore,
        });
        onSubmitted?.();
        return;
      }

      if (!hasPdf) {
        sendCorrection(null);
        return;
      }

      const file = await buildFinalPdfFile(latestAnnotated);
      if (!file) {
        toast.info("No PDF available to submit.");
        return;
      }
      sendCorrection(file);
    } catch (error) {
      toast.error(error?.message || "Failed to build the corrected PDF.");
    } finally {
      if (hasPdf) setIsBuildingPdf(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={hasPdf ? "6xl" : "5xl"} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        {...adminModalContentProps}
        display="flex"
        flexDirection="column"
        maxH={hasPdf ? "96vh" : "90vh"}
        h={hasPdf ? "96vh" : undefined}
        maxW={hasPdf ? "98vw" : undefined}
        my={hasPdf ? 3 : undefined}
        bg="var(--surface-raised)"
        color="var(--text-primary)"
      >
        <ModalHeader
          {...adminModalHeaderProps}
          px={5}
          pt={4}
          pb={3}
          flexShrink={0}
        >
          Correct — {studentLabel}
          {reviewTotal ? (
            <Text as="span" fontSize="sm" fontWeight="700" color="var(--text-muted)" ml={2}>
              ({reviewIndex + 1} of {reviewTotal})
            </Text>
          ) : null}
        </ModalHeader>
        <ModalCloseButton color="var(--text-primary)" />
        <ModalBody
          {...adminModalBodyProps}
          px={4}
          py={3}
          flex="1"
          minH={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {hasPdf ? (
            <Flex flex="1" minH={0} direction={{ base: "column", lg: "row" }} gap={4}>
              <Box
                flex={{ base: "1 1 0", lg: "1 1 72%" }}
                maxW={{ lg: "72%" }}
                minH={{ base: "55vh", lg: 0 }}
                minW={0}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                borderRight={{ lg: "1px solid" }}
                borderColor={{ lg: "var(--border-subtle)" }}
                pr={{ lg: 3 }}
              >
                <Box flex="1" minH={0} overflow="hidden">
                  <HomeworkPdfAnnotator
                    embedded
                    isOpen={isOpen}
                    filename={studentPdfFilename}
                    fetchPdfBytes={fetchExamPdfBytes}
                    modalTitle="Annotate submission"
                    defaultFileName={studentPdfFilename || "exam.pdf"}
                    loadErrorHint="Exam file endpoint not found. Add GET /api/admin/student_exams/file/{filename} on the server."
                    onSave={setAnnotatedFile}
                    saveRef={annotatorSaveRef}
                    pageFeedback={pageFeedback}
                    feedbackContextLabel={feedbackContextLabel}
                    coverPage={resultsCover}
                    toolbarActions={
                      <HStack spacing={2} flexShrink={0}>
                        <Button
                          as="a"
                          href={pdfUrl}
                          target="_blank"
                          size="xs"
                          borderRadius="md"
                          bg="var(--surface-raised)"
                          border="1px solid"
                          borderColor="var(--border-subtle)"
                          color="var(--text-primary)"
                          fontWeight="800"
                          _hover={{ borderColor: brandPalette.primary }}
                        >
                          Open PDF
                        </Button>
                        {annotatedFile ? (
                          <Badge colorScheme="green" borderRadius="full" px={2} py={0.5}>
                            Saved
                          </Badge>
                        ) : null}
                      </HStack>
                    }
                  />
                </Box>
              </Box>

              <Box {...scrollPanelProps}>
                <Text fontSize="sm" fontWeight="800" color="var(--text-primary)" mb={3}>
                  Grading
                </Text>
                {renderGradingForm()}
              </Box>
            </Flex>
          ) : (
            <Box {...scrollPanelProps}>{renderGradingForm()}</Box>
          )}
        </ModalBody>
        <ModalFooter
          gap={3}
          px={5}
          py={3}
          flexShrink={0}
          flexWrap="wrap"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <Box flex="1" minW="220px" maxW="360px" mr="auto">
            <FormLabel fontSize="xs" fontWeight="700" mb={1}>
              Assistant
            </FormLabel>
            <Select
              styles={modalSelectStyles}
              options={assistantsOptions}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              value={assistantsOptions?.find((opt) => opt.value === assistant) || null}
              onChange={(e) => setAssistant(e?.value ?? "")}
              placeholder="Select assistant"
              isClearable
            />
          </Box>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleClose} fontWeight="700">
              {reviewTotal ? "End review" : "Cancel"}
            </Button>
            {onSkip ? (
              <Button
                variant="outline"
                borderColor="var(--border-subtle)"
                fontWeight="700"
                onClick={onSkip}
                isDisabled={onBackgroundSubmit ? false : isSubmittingCorrection || isBuildingPdf}
              >
                Skip
              </Button>
            ) : null}
            {typeof onRefuse === "function" ? (
              <Button
                variant="outline"
                borderColor="red.200"
                color="red.600"
                fontWeight="700"
                onClick={() => onRefuse(studentExam)}
                isDisabled={isSubmittingCorrection || isBuildingPdf}
              >
                Refuse
              </Button>
            ) : null}
            <Button
              bg={brandPalette.primary}
              color="white"
              fontWeight="700"
              onClick={handleSubmit}
              isLoading={onBackgroundSubmit ? false : isSubmittingCorrection || isBuildingPdf}
              loadingText={isBuildingPdf && !isSubmittingCorrection ? "Building PDF..." : "Uploading..."}
              isDisabled={isQuestionsLoading || !correctionQuestions?.length || (!onBackgroundSubmit && isBuildingPdf)}
              _hover={{ bg: brandPalette.secondary }}
            >
              {reviewTotal && reviewIndex < reviewTotal - 1
                ? "Send & next"
                : "Send correction"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CorrectExamModal;
